import { useState, useEffect } from "react";
import { Col, Modal, Button, Input, Row, Select, Checkbox } from "antd";
import { InsightProps } from "./insights";
import InfoTooltip from "@/components/UI/InfoTooltip";
import MarkdownRenderer from "@/components/UI/MarkdownRenderer";
import { useScopeParams } from "@/hooks/useScopeParams";
import { useSuperuserMode } from "@/contexts/useSuperuser";
import { useUser } from "@/contexts/UserContext";
import { getColumnTitle } from "@/utils/tableHelpers";
import { getUserRoleForTrial } from "./insights.utils";

type InsightModalProps = {
  mode: "edit" | "create";
  isOpen: boolean;
  onClose: () => void;
  onSubmit: ((id: string, payload: any) => void) | ((payload: any) => void);
  insight: InsightProps;
  confirmLoading?: boolean;
};

export default function InsightModal({
  isOpen,
  onClose,
  onSubmit,
  insight,
  confirmLoading,
  mode = "edit",
}: InsightModalProps) {
  const { id: trialId } = useScopeParams();
  const { isSuperuserMode } = useSuperuserMode();
  const { userInfo } = useUser();
  const [errorInput, setErrorInput] = useState(false);
  const [formData, setFormData] = useState({
    title: insight?.title ? insight.title : "",
    av_question: insight?.av_question ? insight.av_question : "",
    av_answer: insight?.av_answer ? insight.av_answer : "",
    category: "uncategorized",
    cadence: "daily",
    is_shared: false,
  });

  const trialRole = getUserRoleForTrial(userInfo, trialId);

  useEffect(() => {
    if (isOpen && insight) {
      setFormData({
        title: insight.title,
        av_question: insight.av_question,
        av_answer: insight.av_answer,
        category: insight.category,
        cadence: insight.cadence?.toLowerCase(),
        is_shared: insight.is_shared || false,
      });
    }
  }, [isOpen, insight]);

  const handleSubmit = () => {
    if (formData.title.trim() === "") {
      setErrorInput(true);
      return;
    }
    const payload = {
      category: formData.category,
      title: formData.title,
      cadence: formData.cadence,
      is_shared: formData.is_shared,
      ...(mode === "create" && {
        trial: trialId,
        av_question: insight.av_question,
        askvivo_answer: insight.av_answer,
      }),
    };
    if (mode === "edit") {
      (onSubmit as (id: string, payload: unknown) => void)(insight.id, payload);
    } else {
      (onSubmit as (payload: unknown) => void)(payload);
    }
  };

  return (
    <div>
      <Modal
        title={mode === "edit" ? "Edit Insight" : "Create Insight"}
        open={isOpen}
        width={776}
        centered
        onCancel={onClose}
        destroyOnHidden={true}
        closable={true}
        footer={
          <div className="flex justify-between items-center mt-8">
            <Button key="close" onClick={onClose}>
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={handleSubmit}
              disabled={formData.title?.trim() === ""}
              loading={confirmLoading}
            >
              Confirm
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-2 mt-5">
          <h2 className="text-[14px] text-[#00000073]">
            Generate insights from AskVivo queries & receive updates for a more
            personalized experience.
          </h2>
          <div className="flex flex-col gap-6 pt-2">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm  text-[#064C68]">
                  Insight name
                </label>
                <InfoTooltip title="A user-editable display name for your insight. This name does not affect the Ask Vivo response." />
              </div>
              <Input
                data-testid="edit-insight-name-input"
                value={formData.title}
                status={errorInput ? "error" : ""}
                className={`w-full p-2 rounded ${
                  errorInput ? "bg-[#ffd8d880]! border! border-[#980000]!" : ""
                }`}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, title: value });
                  if (value.trim() === "") {
                    setErrorInput(true);
                  } else {
                    setErrorInput(false);
                  }
                }}
                placeholder="Enter insight name"
              />
              {errorInput && (
                <p className="text-[#980000] text-xs mt-1">
                  Please enter an insight name
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm  text-[#064C68]">
                  Insight prompt
                </label>
                <InfoTooltip title="The prompt used to generate the insight. This is the question you asked AskVivo. This cannot be edited." />
              </div>
              <div className="w-full p-2 border border-[#0000001f] bg-[#00000005] text-gray-800 rounded-sm max-h-52 overflow-y-auto">
                <div data-testid="edit-insight-prompt">
                  <MarkdownRenderer
                    content={
                      insight?.av_question
                        ? insight.av_question
                        : "Not Available"
                    }
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm text-[#064C68]">
                    Insight response
                  </label>
                  <InfoTooltip title="This is the insight response to the insight prompt, generated by Ask Vivo, and updated at the set cadence. This cannot be edited." />
                </div>
              </div>
              <div className="relative">
                <div className="w-full p-2 border border-[#0000001f] bg-[#00000005] text-gray-800 rounded-sm max-h-52 overflow-y-auto">
                  <div data-testid="edit-insight-response">
                    <MarkdownRenderer
                      content={
                        insight?.av_answer ? insight.av_answer : "Not Available"
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Row gutter={16}>
                <Col span={12}>
                  <label className="block text-sm text-[#064C68] mb-2">
                    Select where to view insight
                  </label>
                  <Select
                    data-testid="edit-insight-category-select"
                    value={formData.category}
                    onChange={(value) =>
                      setFormData({ ...formData, category: value })
                    }
                    className="w-full"
                    defaultValue={"uncategorized"}
                    options={[
                      { value: "uncategorized", label: "Uncategorized" },
                      { value: "sites", label: "Sites" },
                      { value: "subjects", label: "Subjects" },
                      { value: "safety", label: "Safety" },
                      { value: "clinical", label: "Clinical" },
                      { value: "data", label: "Data" },
                    ]}
                  />
                </Col>
                <Col span={12}>
                  <label className="block text-sm text-[#064C68] mb-2">
                    Select a cadence for insight updates
                  </label>
                  <Select
                    data-testid="edit-insight-cadence-select"
                    value={formData.cadence}
                    onChange={(value) =>
                      setFormData({ ...formData, cadence: value })
                    }
                    className="w-full"
                    defaultValue={"daily"}
                    options={[
                      { value: "daily", label: "Daily" },
                      { value: "weekly", label: "Weekly" },
                      { value: "monthly", label: "Monthly" },
                    ]}
                  />
                </Col>
              </Row>
            </div>
            {isSuperuserMode && (
              <div className="flex items-center gap-2 mt-4">
                <Checkbox
                  id="share-insight-checkbox"
                  data-testid="edit-insight-share-checkbox"
                  checked={formData.is_shared || false}
                  onChange={(e) =>
                    setFormData({ ...formData, is_shared: e.target.checked })
                  }
                />
                <label
                  htmlFor="share-insight-checkbox"
                  className="text-sm text-[#2B4752]"
                >
                  Share with{" "}
                  <span className="font-bold">
                    {trialRole ? getColumnTitle(trialRole) : "users"}
                  </span>{" "}
                  role users.
                </label>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
